import { useState, useRef, useEffect, useCallback } from "react"
import { api } from "aldoo-ra/API"
import { XMarkIcon, DocumentArrowUpIcon, SparklesIcon } from "@heroicons/react/24/outline"
import { twMerge } from "tailwind-merge"
import CropEditor from "./crop-editor"
import AudioPlayer from "./audio-player"
import AIImageDialog from "./ai-image-dialog"
import TrimTimeline from "./trim-timeline"
import Checkbox from "aldoo-ra/Checkbox"

const cmsAPI = api("cms")

function VideoThumb({ src }) {
  const [thumb, setThumb] = useState(null)
  const [aspect, setAspect] = useState(1)
  useEffect(() => {
    if (!src) return
    const video = document.createElement("video")
    video.src = src
    video.muted = true
    video.preload = "metadata"
    video.onseeked = () => {
      const w = video.videoWidth || 80
      const h = video.videoHeight || 50
      const canvas = document.createElement("canvas")
      canvas.width = w
      canvas.height = h
      canvas.getContext("2d").drawImage(video, 0, 0, w, h)
      setAspect(w / h)
      setThumb(canvas.toDataURL("image/jpeg", 0.7))
    }
    video.onloadedmetadata = () => { video.currentTime = 0.1 }
  }, [src])
  const h = 50
  const w = Math.round(h * aspect)
  return thumb
    ? <img src={thumb} alt="" className="rounded shrink-0 border border-admin_bordered object-cover" style={{ width: w, height: h }} />
    : <FileTypeIcon type="video" className="w-4 h-4 shrink-0 text-admin_text_secondary" />
}
const ffmpegAPI = api("ffmpeg")

let _idSeq = 0
const nextId = () => ++_idSeq

function getCategoryFromExtension(filename) {
  const ext = filename.split(".").pop().toLowerCase()
  if (["jpg", "jpeg", "png", "gif", "svg", "webp", "bmp", "tiff"].includes(ext)) return "images"
  if (["mp4", "avi", "mov", "wmv", "flv", "mkv", "webm", "mpeg", "m4v"].includes(ext)) return "videos"
  if (["mp3", "wav", "ogg", "aac", "flac", "m4a", "wma"].includes(ext)) return "audio"
  return "files"
}

function getFileType(file) {
  if (file.type.startsWith("image/")) return "image"
  if (file.type.startsWith("audio/")) return "audio"
  if (file.type.startsWith("video/")) return "video"
  return "document"
}

function formatBytes(bytes) {
  if (!bytes) return ""
  if (bytes < 1024) return `${bytes}B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)}KB`
  return `${(bytes / 1024 / 1024).toFixed(1)}MB`
}

function FileTypeIcon({ type, className = "w-4 h-4" }) {
  if (type === "image") return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
    </svg>
  )
  if (type === "audio") return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 9l10.5-3m0 6.553v3.75a2.25 2.25 0 01-1.632 2.163l-1.32.377a1.803 1.803 0 11-.99-3.467l2.31-.66a2.25 2.25 0 001.632-2.163zm0 0V2.25L9 5.25v10.303m0 0v3.75a2.25 2.25 0 01-1.632 2.163l-1.32.377a1.803 1.803 0 01-.99-3.467l2.31-.66A2.25 2.25 0 009 15.553z" />
    </svg>
  )
  if (type === "video") return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25h-9A2.25 2.25 0 002.25 7.5v9a2.25 2.25 0 002.25 2.25z" />
    </svg>
  )
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
    </svg>
  )
}

function mimeToExt(mimeType) {
  const map = {
    "image/jpeg": "jpg",
    "image/jpg":  "jpg",
    "image/png":  "png",
    "image/gif":  "gif",
    "image/webp": "webp",
    "image/avif": "avif",
    "image/bmp":  "bmp",
    "image/tiff": "tiff",
    "image/svg+xml": "svg",
  }
  return map[mimeType] || "jpg"
}

export default function FileUpload({ onClose, onUploadComplete }) {
  const [entries, setEntries] = useState([])
  const [selectedId, setSelectedId] = useState(null)
  const [isDragging, setIsDragging] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [uploadedCount, setUploadedCount] = useState(0)
  const [isComplete, setIsComplete] = useState(false)
  const [aiOpen, setAiOpen] = useState(false)
  const [saveAsWebp, setSaveAsWebp] = useState(false)
  // per-entry trim state: { [id]: { start, end, duration } }
  const [trimState, setTrimState] = useState({})
  // per-entry media ref for playhead sync
  const mediaRefs = useRef({})
  // tracks whether the playhead was inside the trim zone on the previous tick
  const wasInsideTrimRef = useRef({})
  const fileInputRef = useRef(null)
  const cropRefs = useRef({})

  const selectedEntry = entries.find((e) => e.id === selectedId) || null
  const selectedTrim = selectedEntry ? trimState[selectedEntry.id] : null

  // Clipboard paste
  useEffect(() => {
    const onPaste = (e) => {
      if (uploading) return
      const items = e.clipboardData?.items
      if (!items) return
      for (const item of items) {
        if (item.type.startsWith("image/")) {
          const file = item.getAsFile()
          if (file) addFiles([file])
          return
        }
      }
    }
    window.addEventListener("paste", onPaste)
    return () => window.removeEventListener("paste", onPaste)
  }, [uploading])

  const addFiles = useCallback((fileList) => {
    const newEntries = Array.from(fileList).map((file) => {
      const id = nextId()
      const type = getFileType(file)
      const previewUrl = (type === "image" || type === "audio" || type === "video")
        ? URL.createObjectURL(file)
        : null
      return { id, file, name: file.name, type, previewUrl }
    })
    setEntries((prev) => [...prev, ...newEntries])
    if (newEntries.length > 0) setSelectedId(newEntries[0].id)
    setIsComplete(false)
  }, [])

  const addImageFromDataUrl = (dataUrl) => {
    const id = nextId()
    fetch(dataUrl)
      .then((r) => r.blob())
      .then((blob) => {
        const file = new File([blob], `ai-generated-${id}.webp`, { type: "image/webp" })
        const previewUrl = URL.createObjectURL(blob)
        setEntries((prev) => [...prev, { id, file, name: file.name, type: "image", previewUrl }])
        setSelectedId(id)
        setIsComplete(false)
      })
  }

  const removeEntry = (id) => {
    setEntries((prev) => {
      const entry = prev.find((e) => e.id === id)
      if (entry?.previewUrl) URL.revokeObjectURL(entry.previewUrl)
      delete cropRefs.current[id]
      delete mediaRefs.current[id]
      return prev.filter((e) => e.id !== id)
    })
    setTrimState((prev) => { const n = { ...prev }; delete n[id]; return n })
    setSelectedId((prev) => {
      if (prev !== id) return prev
      const remaining = entries.filter((e) => e.id !== id)
      return remaining.length > 0 ? remaining[0].id : null
    })
  }

  // When a media element loads metadata, initialise trim state for that entry
  const handleMediaLoaded = (id, duration) => {
    if (!duration) return
    setTrimState((prev) => {
      if (prev[id]) return prev // already set
      return { ...prev, [id]: { start: 0, end: duration, duration } }
    })
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setIsDragging(false)
    if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files)
  }

  // Returns true if a trim is active (region != full duration)
  const hasTrim = (id) => {
    const t = trimState[id]
    if (!t) return false
    return t.start > 0.01 || t.end < t.duration - 0.01
  }

  const handleUpload = async () => {
    if (!entries.length) return
    setUploading(true)
    setUploadProgress(0)
    setUploadedCount(0)
    setIsComplete(false)

    let processed = 0
    const responses = []

    try {
      for (const entry of entries) {
        let file = entry.file
        let category = getCategoryFromExtension(entry.name)

        // --- Image: apply crop and/or WebP conversion ---
        if (entry.type === "image" && cropRefs.current[entry.id]) {
          const editor = cropRefs.current[entry.id]
          const hasCrop = editor.isCropped()
          if (hasCrop || saveAsWebp) {
            try {
              const originalMime = entry.file.type || "image/jpeg"
              const outputMime = saveAsWebp ? "image/webp" : originalMime
              const outputExt  = mimeToExt(outputMime)
              const blob = await editor.crop(outputMime)
              const baseName = entry.name.replace(/\.[^.]+$/, "") || "image"
              file = new File([blob], `${baseName}.${outputExt}`, { type: outputMime })
              category = "images"
            } catch { /* fall through */ }
          }
        }

        // --- Audio/Video: upload then trim in-place via server-side ffmpeg ---
        if ((entry.type === "audio" || entry.type === "video") && hasTrim(entry.id)) {
          const trim = trimState[entry.id]
          // 1. Upload the original file
          const uploadFormData = new FormData()
          uploadFormData.append("file_upload", "1")
          uploadFormData.append("category", category)
          uploadFormData.append("file", file)
          const uploadRes = await cmsAPI(uploadFormData, {
            headers: { "Content-Type": "multipart/form-data" },
          })
          if (uploadRes?.error) throw new Error(uploadRes.error)

          // 2. Trim in-place — server overwrites the CMS file and returns same path
          const trimRes = await ffmpegAPI({
            trim: {
              filePath: uploadRes.path,
              start: trim.start,
              end: trim.end,
            },
          })
          if (trimRes?.error) {
            console.warn("[FileUpload] trim failed, keeping original:", trimRes.error)
          }

          processed++
          setUploadedCount(processed)
          setUploadProgress((processed / entries.length) * 100)
          responses.push(trimRes?.result ? trimRes : uploadRes)
          continue
        }

        // --- Normal upload ---
        const formData = new FormData()
        formData.append("file_upload", "1")
        formData.append("category", category)
        formData.append("file", file)

        const res = await cmsAPI(formData, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (evt) => {
            const overall = ((processed + evt.loaded / evt.total) / entries.length) * 100
            setUploadProgress(overall)
          },
        })

        processed++
        setUploadedCount(processed)
        setUploadProgress((processed / entries.length) * 100)
        responses.push(res)
      }

      setIsComplete(true)
      onUploadComplete && onUploadComplete(responses)
    } catch (err) {
      console.error("Upload error:", err)
    } finally {
      setUploading(false)
    }
  }

  const handleButtonClick = () => {
    if (isComplete) onClose?.()
    else handleUpload()
  }

  const containerHeight = "h-[calc(100vh-300px)] min-h-[360px]"

  return (
    <div className="h-full flex flex-col" onClick={(e) => e.stopPropagation()}>
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-admin_bordered shrink-0">
        <span className="text-admin_text font-semibold text-lg">Upload Files</span>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setAiOpen(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-admin_bordered text-admin_text_secondary text-sm font-medium hover:border-admin_primary hover:text-admin_primary transition"
          >
            <SparklesIcon className="w-4 h-4" />
            AI Generate
          </button>
          <button
            onClick={() => fileInputRef.current?.click()}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-admin_primary text-white text-sm font-medium hover:bg-admin_primary_dk transition"
          >
            <DocumentArrowUpIcon className="w-4 h-4" />
            Choose files
          </button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*,audio/*,video/*,application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip"
            className="hidden"
            onChange={(e) => { if (e.target.files?.length) addFiles(e.target.files); e.target.value = "" }}
          />
        </div>
      </div>

      {/* Main: preview left, file list right */}
      <div className="flex-1 flex overflow-hidden">

        {/* Left: preview + trim timeline */}
        <div className={twMerge("w-1/2 border-r border-admin_bordered flex flex-col overflow-hidden", containerHeight)}>
          {selectedEntry ? (
            <div className="flex-1 flex flex-col overflow-hidden">

              {/* Image → crop editor */}
              {selectedEntry.type === "image" && selectedEntry.previewUrl && (
                <div className="flex-1 flex flex-col overflow-hidden p-3 gap-2">
                  <CropEditor
                    ref={(r) => { if (r) cropRefs.current[selectedEntry.id] = r; else delete cropRefs.current[selectedEntry.id] }}
                    src={selectedEntry.previewUrl}
                    height={null}
                    className="flex-1"
                  />
                  <p className="text-xs text-admin_text_secondary text-center shrink-0">
                    Drag handles to crop — applied on upload
                  </p>
                </div>
              )}

              {/* Audio → player + trim timeline */}
              {selectedEntry.type === "audio" && selectedEntry.previewUrl && (
                <div className="flex-1 flex flex-col overflow-hidden p-3 gap-3">
                  <div className="shrink-0">
                    <audio
                      ref={(r) => { mediaRefs.current[selectedEntry.id] = r }}
                      src={selectedEntry.previewUrl}
                      controls
                      preload="metadata"
                      className="w-full"
                      onLoadedMetadata={(e) => handleMediaLoaded(selectedEntry.id, e.target.duration)}
                      onTimeUpdate={(e) => {
                        const t = trimState[selectedEntry.id]
                        if (!t || e.target.paused || e.target.seeking) return
                        const ct = e.target.currentTime
                        const id = selectedEntry.id
                        const wasInside = wasInsideTrimRef.current[id] ?? false
                        const isInside = ct >= t.start && ct < t.end
                        wasInsideTrimRef.current[id] = isInside
                        // Only stop if we were inside and just hit the end boundary
                        if (wasInside && ct >= t.end) {
                          e.target.pause()
                          e.target.currentTime = t.end
                        }
                      }}
                    />
                  </div>
                  {selectedTrim && (
                    <div className="shrink-0 px-1">
                      <TrimTimeline
                        file={selectedEntry.file}
                        type="audio"
                        duration={selectedTrim.duration}
                        start={selectedTrim.start}
                        end={selectedTrim.end}
                        onTrimChange={({ start, end }) =>
                          setTrimState((prev) => ({ ...prev, [selectedEntry.id]: { ...prev[selectedEntry.id], start, end } }))
                        }
                        onSeek={(t) => { const el = mediaRefs.current[selectedEntry.id]; if (el) el.currentTime = t }}
                        mediaRef={{ current: mediaRefs.current[selectedEntry.id] }}
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Video → player + trim timeline */}
              {selectedEntry.type === "video" && selectedEntry.previewUrl && (
                <div className="flex-1 flex flex-col overflow-hidden p-3 gap-3">
                  <div className="flex-1 min-h-0 overflow-hidden">
                    <video
                      ref={(r) => { mediaRefs.current[selectedEntry.id] = r }}
                      src={selectedEntry.previewUrl}
                      controls
                      preload="metadata"
                      className="w-full h-full rounded-xl bg-black object-contain"
                      onLoadedMetadata={(e) => handleMediaLoaded(selectedEntry.id, e.target.duration)}
                      onTimeUpdate={(e) => {
                        const t = trimState[selectedEntry.id]
                        if (!t || e.target.paused || e.target.seeking) return
                        const ct = e.target.currentTime
                        const id = selectedEntry.id
                        const wasInside = wasInsideTrimRef.current[id] ?? false
                        const isInside = ct >= t.start && ct < t.end
                        wasInsideTrimRef.current[id] = isInside
                        // Only stop if we were inside and just hit the end boundary
                        if (wasInside && ct >= t.end) {
                          e.target.pause()
                          e.target.currentTime = t.end
                        }
                      }}
                    />
                  </div>
                  {selectedTrim && (
                    <div className="shrink-0 px-1">
                      <TrimTimeline
                        file={selectedEntry.file}
                        type="video"
                        duration={selectedTrim.duration}
                        start={selectedTrim.start}
                        end={selectedTrim.end}
                        onTrimChange={({ start, end }) =>
                          setTrimState((prev) => ({ ...prev, [selectedEntry.id]: { ...prev[selectedEntry.id], start, end } }))
                        }
                        onSeek={(t) => { const el = mediaRefs.current[selectedEntry.id]; if (el) el.currentTime = t }}
                        mediaRef={{ current: mediaRefs.current[selectedEntry.id] }}
                      />
                    </div>
                  )}
                </div>
              )}

              {selectedEntry.type === "document" && (
                <div className="flex-1 flex flex-col items-center justify-center gap-3 text-admin_text_secondary">
                  <FileTypeIcon type="document" className="w-12 h-12 opacity-40" />
                  <span className="text-sm text-center px-4 truncate max-w-full">{selectedEntry.name}</span>
                </div>
              )}
            </div>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center gap-2 text-admin_text_secondary">
              <FileTypeIcon type="document" className="w-10 h-10 opacity-30" />
              <span className="text-sm">Select a file to preview</span>
            </div>
          )}
        </div>

        {/* Right: file list (drop zone) */}
        <div
          className={twMerge(
            "w-1/2 flex flex-col overflow-hidden transition-colors",
            containerHeight,
            isDragging && "bg-admin_primary/5"
          )}
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true) }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
        >
          {entries.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center gap-3 text-admin_text_secondary p-6">
              <DocumentArrowUpIcon className="w-10 h-10 opacity-30" />
              <span className="text-sm text-center">Drop files here, choose files, or paste an image (⌘V)</span>
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto p-3 space-y-1.5">
              {entries.map((entry) => {
                const trim = trimState[entry.id]
                const trimmed = hasTrim(entry.id)
                return (
                  <div
                    key={entry.id}
                    onClick={() => setSelectedId(entry.id)}
                    className={twMerge(
                      "flex items-center gap-2 px-2 py-1.5 rounded-lg cursor-pointer transition group",
                      selectedId === entry.id
                        ? "bg-admin_primary/10 border border-admin_primary/30"
                        : "hover:bg-admin_canvas border border-transparent"
                    )}
                  >
                    {(entry.type === "image" || entry.type === "video") && entry.previewUrl ? (
                      entry.type === "image" ? (
                        <img src={entry.previewUrl} alt="" className="w-[50px] h-[50px] rounded object-cover shrink-0 border border-admin_bordered" />
                      ) : (
                        <VideoThumb src={entry.previewUrl} />
                      )
                    ) : (
                      <FileTypeIcon type={entry.type} className="w-4 h-4 shrink-0 text-admin_text_secondary" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-admin_text truncate">{entry.name}</p>
                      <p className="text-xs text-admin_text_secondary">
                        {formatBytes(entry.file.size)}
                        {trimmed && trim && (
                          <span className="ml-1.5 text-admin_primary">
                            · {trim.start.toFixed(1)}s–{trim.end.toFixed(1)}s
                          </span>
                        )}
                      </p>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); removeEntry(entry.id) }}
                      className="opacity-0 group-hover:opacity-100 transition p-1 rounded-full hover:bg-admin_error/10 text-admin_text_secondary hover:text-admin_error shrink-0"
                    >
                      <XMarkIcon className="w-4 h-4" />
                    </button>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="px-6 py-4 border-t border-admin_bordered bg-admin_paper flex items-center justify-between gap-4 shrink-0">
        <div className="flex items-center gap-3 text-sm text-admin_text_secondary">
          <span>{uploadedCount} of {entries.length} files uploaded</span>
          {entries.length > 0 && !uploading && !isComplete && (
            <button
              onClick={() => {
                entries.forEach((e) => { if (e.previewUrl) URL.revokeObjectURL(e.previewUrl) })
                cropRefs.current = {}
                mediaRefs.current = {}
                setEntries([])
                setSelectedId(null)
                setTrimState({})
              }}
              className="text-xs underline hover:text-admin_text transition"
            >
              Clear all
            </button>
          )}
        </div>

        {(uploading || isComplete) && (
          <div className="flex-1 max-w-xs">
            <div className="w-full h-1.5 bg-admin_neutral_1 rounded-full overflow-hidden">
              <div
                className={twMerge("h-full transition-all duration-300 rounded-full", isComplete ? "bg-admin_success" : "bg-admin_primary")}
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
          </div>
        )}

        <div className="flex items-center gap-3 shrink-0">
          <Checkbox
            variant="admin_compact"
            label="Import as WebP"
            isOn={saveAsWebp}
            onChange={setSaveAsWebp}
          />
          <button
            disabled={uploading || entries.length === 0}
            onClick={handleButtonClick}
            className="px-5 py-2 rounded-lg bg-admin_primary text-white text-sm font-medium hover:bg-admin_primary_dk transition disabled:opacity-40"
          >
            {isComplete ? "Done" : uploading ? "Uploading…" : "Upload Files"}
          </button>
        </div>
      </div>

      {aiOpen && (
        <AIImageDialog
          onUse={(dataUrl) => { setAiOpen(false); addImageFromDataUrl(dataUrl) }}
          onClose={() => setAiOpen(false)}
        />
      )}
    </div>
  )
}
